<template>
  <div class="api-keys-page">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h2>API Keys</h2>
        <!-- Organization Filter -->
        <div v-if="organizationIds.length > 1" class="mt-2">
          <label for="organization-select" class="form-label">
            Organization:
          </label>
          <b-form-select
            id="organization-select"
            v-model="selectedOrganizationId"
            :options="organizationOptions"
            class="w-auto"
            @change="onOrganizationChange"
          />
        </div>
      </div>
      <b-button
        variant="primary"
        :disabled="loading"
        @click="showCreateModal = true"
      >
        <b-icon icon="plus"></b-icon> Create API Key
      </b-button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-4">
      <b-spinner variant="primary"></b-spinner>
      <p class="mt-2">Loading API keys...</p>
    </div>

    <!-- Error State -->
    <b-alert
      v-if="error"
      variant="danger"
      show
      dismissible
      @dismissed="error = null"
    >
      {{ error }}
    </b-alert>

    <!-- API Keys Table -->
    <div v-if="!loading && apiKeys.length > 0">
      <div v-if="organizationIds.length > 1" class="mb-2">
        <small class="text-muted">
          Showing API keys for
          <strong>{{ getCurrentOrganizationName() }}</strong>
        </small>
      </div>
      <b-table
        :items="apiKeys"
        :fields="tableFields"
        striped
        hover
        responsive
        :busy="loading"
      >
        <!-- Name Column -->
        <template #cell(name)="row">
          <div>
            <strong>{{ row.item.name }}</strong>
            <div v-if="row.item.description" class="text-muted small">
              {{ row.item.description }}
            </div>
          </div>
        </template>

        <!-- API Key Column -->
        <template #cell(masked_key)="row">
          <code class="api-key-display">{{ row.item.masked_key }}</code>
        </template>

        <!-- Status Column -->
        <template #cell(is_active)="row">
          <b-badge :variant="row.item.is_active ? 'success' : 'secondary'">
            {{ row.item.is_active ? 'Active' : 'Inactive' }}
          </b-badge>
        </template>

        <!-- Last Used Column -->
        <template #cell(last_used_at)="row">
          <span v-if="row.item.last_used_at">
            {{ formatDate(row.item.last_used_at) }}
          </span>
          <span v-else class="text-muted">Never</span>
        </template>

        <!-- Created At Column -->
        <template #cell(created_at)="row">
          {{ formatDate(row.item.created_at) }}
        </template>

        <!-- Actions Column -->
        <template #cell(actions)="row">
          <div class="d-flex gap-2">
            <b-button
              variant="outline-primary"
              size="sm"
              title="Edit API Key"
              @click="editApiKey(row.item)"
            >
              <b-icon icon="pencil-square"></b-icon>
            </b-button>
            <b-button
              variant="outline-danger"
              size="sm"
              title="Delete API Key"
              @click="confirmDelete(row.item)"
            >
              <b-icon icon="trash"></b-icon>
            </b-button>
          </div>
        </template>
      </b-table>

      <!-- Pagination -->
      <b-pagination
        v-if="pagination.total > pagination.per_page"
        v-model="currentPage"
        :total-rows="pagination.total"
        :per-page="pagination.per_page"
        class="mt-3"
        @change="loadApiKeys"
      />
    </div>

    <!-- Empty State -->
    <div v-if="!loading && apiKeys.length === 0" class="text-center py-5">
      <b-icon icon="key" variant="muted" scale="3" class="mb-3"></b-icon>
      <h4 class="text-muted">No API Keys Found</h4>
      <p class="text-muted">
        <span v-if="organizationIds.length > 1">
          No API keys found for
          <strong>{{ getCurrentOrganizationName() }}</strong>
          .
        </span>
        <span v-else>
          Create your first API key to get started with the API.
        </span>
      </p>
      <b-button variant="primary" @click="showCreateModal = true">
        <b-icon icon="plus"></b-icon> Create API Key
      </b-button>
    </div>

    <!-- Create/Edit Modal -->
    <b-modal
      v-model="showCreateModal"
      :title="editingApiKey ? 'Edit API Key' : 'Create API Key'"
      size="md"
      @hidden="resetForm"
    >
      <div v-if="organizationIds.length > 1" class="mb-3">
        <small class="text-muted">
          Creating API key for
          <strong>{{ getCurrentOrganizationName() }}</strong>
        </small>
      </div>
      <b-form @submit.prevent="saveApiKey">
        <b-form-group label="Name" label-for="api-key-name">
          <b-form-input
            id="api-key-name"
            v-model="form.name"
            placeholder="Enter API key name"
            required
            :state="formErrors.name ? false : null"
          />
          <b-form-invalid-feedback v-if="formErrors.name">
            {{ formErrors.name }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group label="Description" label-for="api-key-description">
          <b-form-textarea
            id="api-key-description"
            v-model="form.description"
            placeholder="Enter description (optional)"
            rows="3"
          />
        </b-form-group>

        <b-form-group v-if="editingApiKey" label="Status">
          <b-form-checkbox v-model="form.is_active"> Active </b-form-checkbox>
        </b-form-group>
      </b-form>

      <template #modal-footer>
        <b-button variant="secondary" @click="showCreateModal = false">
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :disabled="saving || !form.name"
          @click="saveApiKey"
        >
          <b-spinner v-if="saving" small class="mr-2"></b-spinner>
          {{ editingApiKey ? 'Update' : 'Create' }}
        </b-button>
      </template>
    </b-modal>

    <!-- New API Key Display Modal -->
    <b-modal
      v-model="showNewKeyModal"
      title="API Key Created"
      size="lg"
      @hidden="showNewKeyModal = false"
    >
      <div class="alert alert-warning">
        <b-icon icon="exclamation-triangle"></b-icon>
        <strong>Important:</strong> This is the only time you'll be able to see
        the full API key. Make sure to copy and store it securely.
      </div>

      <div class="text-center">
        <h5 class="mb-3">Your API Key</h5>
        <code class="api-key-display d-block p-3 bg-light border rounded">{{
          newApiKey
        }}</code>
      </div>

      <template #modal-footer>
        <b-button variant="primary" @click="showNewKeyModal = false">
          Done
        </b-button>
      </template>
    </b-modal>

    <!-- Delete Confirmation Modal -->
    <b-modal
      v-model="showDeleteModal"
      title="Delete API Key"
      @hidden="resetDeleteForm"
    >
      <p>
        Are you sure you want to delete the API key
        <strong>{{ deletingApiKey && deletingApiKey.name }}</strong
        >?
      </p>
      <p class="text-danger">
        <b-icon icon="exclamation-triangle"></b-icon>
        This action cannot be undone and will immediately invalidate the API
        key.
      </p>

      <template #modal-footer>
        <b-button variant="secondary" @click="showDeleteModal = false">
          Cancel
        </b-button>
        <b-button variant="danger" :disabled="deleting" @click="deleteApiKey">
          <b-spinner v-if="deleting" small class="mr-2"></b-spinner>
          Delete
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ApiKeysAPI from '../services/ApiKeysAPI';
import OrganizationsAPI from '../services/OrganizationsAPI';

export default {
  name: 'ApiKeys',
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      error: null,
      apiKeys: [],
      pagination: {
        total: 0,
        per_page: 25,
        current_page: 1,
      },
      currentPage: 1,
      showCreateModal: false,
      showNewKeyModal: false,
      showDeleteModal: false,
      editingApiKey: null,
      deletingApiKey: null,
      newApiKey: '',
      selectedOrganizationId: null,
      organizations: [],
      form: {
        name: '',
        description: '',
        is_active: true,
      },
      formErrors: {},
      tableFields: [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'masked_key', label: 'API Key', sortable: false },
        { key: 'is_active', label: 'Status', sortable: true },
        { key: 'last_used_at', label: 'Last Used', sortable: true },
        { key: 'created_at', label: 'Created', sortable: true },
        { key: 'actions', label: 'Actions', sortable: false },
      ],
    };
  },
  computed: {
    ...mapGetters('session', ['organizationIds', 'organizationAdmin']),
    currentOrganizationId() {
      return (
        this.selectedOrganizationId ||
        (this.organizationIds.length > 0 ? this.organizationIds[0] : null)
      );
    },
    organizationOptions() {
      return this.organizationIds.map((id) => {
        const org = this.organizations.find((o) => o.id === id);
        return {
          value: id,
          text: org ? org.name || `Organization ${id}` : `Organization ${id}`,
        };
      });
    },
  },
  created() {
    // Check if user has organization admin access
    if (!this.organizationAdmin) {
      this.$router.push('/');
      return;
    }

    // Initialize selected organization to first available
    if (this.organizationIds.length > 0) {
      [this.selectedOrganizationId] = this.organizationIds;
    }

    this.loadOrganizations();
    this.loadApiKeys();
  },
  methods: {
    async loadOrganizations() {
      if (this.organizationIds.length === 0) {
        return;
      }

      try {
        // Try to get all organizations at once
        const response = await OrganizationsAPI.getOrganizations(
          this.organizationIds
        );
        this.organizations = response.data.data || response.data;
      } catch (error) {
        // If array format fails, try comma-separated string format
        try {
          const response = await OrganizationsAPI.getOrganizationsByString(
            this.organizationIds
          );
          this.organizations = response.data.data || response.data;
        } catch (stringError) {
          // If string format fails, try individual requests
          try {
            const promises = this.organizationIds.map((id) =>
              OrganizationsAPI.getOrganization(id)
            );
            const responses = await Promise.all(promises);
            this.organizations = responses.map(
              (response) => response.data.data || response.data
            );
          } catch (individualError) {
            // If all requests fail, we'll fall back to IDs
            this.organizations = [];
          }
        }
      }
    },

    getCurrentOrganizationName() {
      if (!this.currentOrganizationId) {
        return 'Unknown Organization';
      }

      const org = this.organizations.find(
        (o) => o.id === this.currentOrganizationId
      );
      return org
        ? org.name || `Organization ${this.currentOrganizationId}`
        : `Organization ${this.currentOrganizationId}`;
    },

    onOrganizationChange() {
      // Reset pagination and reload API keys for the new organization
      this.currentPage = 1;
      this.loadApiKeys();
    },

    async loadApiKeys() {
      if (!this.currentOrganizationId) {
        this.error = 'No organization access found';
        return;
      }

      this.loading = true;
      this.error = null;

      try {
        const response = await ApiKeysAPI.getApiKeys(
          this.currentOrganizationId,
          {
            page: this.currentPage,
            limit: this.pagination.per_page,
          }
        );

        this.apiKeys = response.data.data;
        this.pagination = {
          total: response.data.total,
          per_page: response.data.per_page,
          current_page: response.data.current_page,
        };
      } catch (error) {
        this.error = error.response?.data?.message || 'Failed to load API keys';
      } finally {
        this.loading = false;
      }
    },

    async saveApiKey() {
      if (!this.currentOrganizationId) {
        this.error = 'No organization access found';
        return;
      }

      this.saving = true;
      this.formErrors = {};

      try {
        if (this.editingApiKey) {
          // Update existing API key
          await ApiKeysAPI.updateApiKey(
            this.currentOrganizationId,
            this.editingApiKey.id,
            this.form
          );
        } else {
          // Create new API key
          const response = await ApiKeysAPI.createApiKey(
            this.currentOrganizationId,
            this.form
          );

          // Handle different possible response structures
          this.newApiKey =
            response.data?.key ||
            response.data?.api_key ||
            response.data?.data?.key ||
            response.data?.data?.api_key;

          if (!this.newApiKey) {
            this.error =
              'API key was created but could not be retrieved. Please refresh the page.';
            return;
          }

          this.showNewKeyModal = true;
        }

        this.showCreateModal = false;
        this.loadApiKeys();
      } catch (error) {
        if (error.response?.data?.errors) {
          this.formErrors = error.response.data.errors;
        } else {
          this.error =
            error.response?.data?.message || 'Failed to save API key';
        }
      } finally {
        this.saving = false;
      }
    },

    async deleteApiKey() {
      if (!this.currentOrganizationId || !this.deletingApiKey) {
        return;
      }

      this.deleting = true;

      try {
        await ApiKeysAPI.deleteApiKey(
          this.currentOrganizationId,
          this.deletingApiKey.id
        );
        this.showDeleteModal = false;
        this.loadApiKeys();
      } catch (error) {
        this.error =
          error.response?.data?.message || 'Failed to delete API key';
      } finally {
        this.deleting = false;
      }
    },

    editApiKey(apiKey) {
      this.editingApiKey = apiKey;
      this.form = {
        name: apiKey.name,
        description: apiKey.description || '',
        is_active: apiKey.is_active,
      };
      this.showCreateModal = true;
    },

    confirmDelete(apiKey) {
      this.deletingApiKey = apiKey;
      this.showDeleteModal = true;
    },

    resetForm() {
      this.editingApiKey = null;
      this.form = {
        name: '',
        description: '',
        is_active: true,
      };
      this.formErrors = {};
    },

    resetDeleteForm() {
      this.deletingApiKey = null;
    },

    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
      } catch (error) {
        // Silently handle clipboard errors
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
};
</script>

<style scoped>
.api-keys-page {
  padding: 20px;
}

.api-key-display {
  background-color: #f8f9fa;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 0.9em;
}

.gap-2 {
  gap: 0.5rem;
}

.font-monospace {
  font-family: 'Courier New', monospace;
}
</style>
